import React, { useState } from 'react';
import { Card } from 'components/Card';
import { AccordionSection } from './AccordionSection';
import {
  TargetingConfigProvider,
  useTargetingSelectionCounts,
  useTargetingConfigContext,
} from './TargetingConfigContext';
import { SociodemographicConfig } from './SociodemographicConfig';
import { LocationsConfig } from './LocationsConfig';
import { InterestsConfig } from './InterestsConfig';
import { B2BConfig } from './B2BConfig';
import { Divider, Typography, Link, Stack } from '@mui/material';
import { Website } from 'types';

const AccordionConfig = [
  {
    key: 'soziodemografisch',
    title: 'Soziodemografisch',
    render: () => <SociodemographicConfig />,
    getCount: (counts: ReturnType<typeof useTargetingSelectionCounts>) => counts.sociodemographicCount,
  },
  {
    key: 'standorte',
    title: 'Standorte',
    render: () => <LocationsConfig />,
    getCount: (counts: ReturnType<typeof useTargetingSelectionCounts>) => counts.locationsCount,
  },
  {
    key: 'interessen',
    title: 'Interessen',
    render: () => <InterestsConfig />,
    getCount: () => undefined,
  },
  {
    key: 'b2b',
    title: 'B2B',
    render: () => <B2BConfig />,
    getCount: (counts: ReturnType<typeof useTargetingSelectionCounts>) => counts.b2bCount,
  },
];

interface TargetingConfigurationProps {
  website?: Website;
  websiteIndex?: number;
}

const TargetingConfigurationContent = () => {
  const selectionCounts = useTargetingSelectionCounts();
  const { resetToDefaults } = useTargetingConfigContext();

  // Calculate total count across all sections
  const totalCount = selectionCounts.sociodemographicCount + selectionCounts.locationsCount + selectionCounts.b2bCount;

  // Accordion state: allow multiple open
  const [expanded, setExpanded] = useState<{ [key: string]: boolean }>({
    soziodemografisch: false,
    standorte: false,
    interessen: false,
    b2b: false,
  });

  const handleAccordionChange = (section: string) => (_: React.SyntheticEvent, isExp: boolean) => {
    setExpanded((prev) => ({ ...prev, [section]: isExp }));
  };

  const handleResetAll = () => {
    resetToDefaults();
  };

  return (
    <>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        gap={{ xs: 2, md: 1 }}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        sx={{ mb: 5 }}
      >
        <Typography variant="subtitle1">Konfiguriere deine Zielgruppe</Typography>
        {totalCount > 0 && (
          <Stack direction="row" alignItems="flex-end">
            <Typography
              variant="subtitle1"
              component="span"
              sx={{
                ml: 1,
                color: 'primary.main',
                fontWeight: 'bold',
              }}
            >
              ({totalCount})
            </Typography>
            <Link href="#" sx={{ ml: 4 }} onClick={handleResetAll}>
              Alle Kriterien zurücksetzen
            </Link>
          </Stack>
        )}
      </Stack>
      <Divider />
      {AccordionConfig.map((section) => (
        <AccordionSection
          key={section.key}
          expanded={expanded[section.key] ?? false}
          onChange={handleAccordionChange(section.key)}
          title={section.title}
          count={section.getCount(selectionCounts)}
        >
          {section.render()}
        </AccordionSection>
      ))}
    </>
  );
};

export const TargetingConfiguration = ({ website, websiteIndex }: TargetingConfigurationProps) => {
  return (
    <TargetingConfigProvider website={website} websiteIndex={websiteIndex}>
      <Card>
        {website?.url ? <Typography sx={{ fontWeight: 700, fontSize: '20px', mb: 5 }}>{website.url}</Typography> : null}
        <TargetingConfigurationContent />
      </Card>
    </TargetingConfigProvider>
  );
};
